import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
import { Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line } from 'recharts';
import { Icons } from '../components/Icons';
import { generateAlerts, getAllActiveAlerts, dismissAlert } from '../utils/alertSystem';
import { getUserDisplayName } from '../utils/userMapping';

function DashboardEnhanced() {
  const [stats, setStats] = useState({
    total: 0,
    totalValue: 0,
    byCategory: {},
    byLocation: {},
    byStatus: {}
  });
  const [salesStats, setSalesStats] = useState({
    thisMonth: 0,
    thisMonthRevenue: 0,
    thisMonthProfit: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  // Professional Color Palette
  const COLORS = ['#0f172a', '#334155', '#64748b', '#94a3b8', '#cbd5e1'];

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllData = async () => {
    try {
      await Promise.all([
        loadInventoryStats(),
        loadSalesStats(),
        loadRecentActivity(),
        loadAlerts(),
        loadMonthlyTrend()
      ]);
    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      // 알림 생성 (최초 또는 정기적으로)
      await generateAlerts();

      // 활성 알림 불러오기
      const activeAlerts = await getAllActiveAlerts();
      setAlerts(activeAlerts);
    } catch (error) {
      console.error('알림 로드 오류:', error);
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await dismissAlert(alertId);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      console.error('알림 무시 처리 오류:', error);
    }
  };

  const loadInventoryStats = async () => {
    const inventoryRef = collection(db, 'inventory');
    const snapshot = await getDocs(inventoryRef);

    const locationCounts = {};
    const statusCounts = {};
    const categoryCounts = {};
    let totalValue = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();

      // 카테고리별 집계 (대분류 기준)
      const category = data.categoryLarge || '미분류';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;

      // 위치별 집계
      const loc = data.location || '미지정';
      locationCounts[loc] = (locationCounts[loc] || 0) + 1;

      // 상태별 집계
      const status = data.status || '판매가능';
      statusCounts[status] = (statusCounts[status] || 0) + 1;

      // 가치 계산 (판매가 기준)
      if (data.status !== '판매완료') {
        totalValue += parseInt(data.salePrice || 0);
      }
    });

    setStats({
      total: snapshot.size,
      totalValue,
      byCategory: categoryCounts,
      byLocation: locationCounts,
      byStatus: statusCounts
    });
  };

  const loadSalesStats = async () => {
    const salesRef = collection(db, 'sales');
    const snapshot = await getDocs(salesRef);

    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    let thisMonth = 0, thisMonthRevenue = 0, thisMonthProfit = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const saleDate = data.saleDate?.toDate?.() || new Date(data.saleDate);

      if (saleDate >= thisMonthStart) {
        thisMonth++;
        thisMonthRevenue += data.actualSalePrice || 0;
        thisMonthProfit += data.profit || 0;
      }
    });

    setSalesStats({ thisMonth, thisMonthRevenue, thisMonthProfit });
  };

  const loadRecentActivity = async () => {
    const historyRef = collection(db, 'inventory_history');
    const q = query(historyRef, orderBy('timestamp', 'desc'), limit(10));
    const snapshot = await getDocs(q);

    // 각 활동에 대해 제품명 가져오기
    const activitiesWithProducts = await Promise.all(
      snapshot.docs.map(async (activityDoc) => {
        const activityData = activityDoc.data();
        let productName = '알 수 없음';

        // productId가 있으면 제품명 가져오기
        if (activityData.productId) {
          try {
            const productDoc = await getDoc(doc(db, 'inventory', activityData.productId));
            if (productDoc.exists()) {
              productName = productDoc.data().productName || '제품명 없음';
            }
          } catch (error) {
            console.error('제품 정보 로드 오류:', error);
          }
        }

        return {
          id: activityDoc.id,
          ...activityData,
          productName,
          timestamp: activityData.timestamp?.toDate?.() || new Date()
        };
      })
    );

    setRecentActivity(activitiesWithProducts);
  };

  const loadMonthlyTrend = async () => {
    // 최근 12개월 배열 생성
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        year: date.getFullYear(),
        month: date.getMonth(),
        label: `${date.getMonth() + 1}월`,
        입고: 0,
        출고: 0,
        재고수량: 0,
        재고금액: 0
      });
    }

    // inventory 컬렉션에서 입고 데이터 집계 (createdAt 기준)
    const inventorySnapshot = await getDocs(collection(db, 'inventory'));
    let totalValue = 0;
    let totalCount = 0;

    inventorySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      const monthData = months.find(m =>
        m.year === createdAt.getFullYear() && m.month === createdAt.getMonth()
      );
      if (monthData) {
        monthData.입고++;
      }
      // 평균 단가 계산용 (판매가 기준)
      if (data.status !== '판매완료') {
        totalValue += parseInt(data.salePrice || 0);
        totalCount++;
      }
    });

    const avgPrice = totalCount > 0 ? totalValue / totalCount : 0;

    // sales 컬렉션에서 출고(판매) 데이터 집계
    const salesSnapshot = await getDocs(collection(db, 'sales'));
    salesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const saleDate = data.saleDate?.toDate?.() || new Date(data.saleDate);
      const monthData = months.find(m =>
        m.year === saleDate.getFullYear() && m.month === saleDate.getMonth()
      );
      if (monthData) {
        monthData.출고++;
      }
    });

    // 12개월 전 시점의 초기 재고 계산
    const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    let runningStock = 0;

    inventorySnapshot.docs.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      if (createdAt < twelveMonthsAgo && data.status !== '판매완료') {
        runningStock++;
      }
    });

    // 판매된 아이템 중 12개월 전 이전 판매 건 제외
    salesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      const saleDate = data.saleDate?.toDate?.() || new Date(data.saleDate);
      if (saleDate < twelveMonthsAgo) {
        runningStock--;
      }
    });

    runningStock = Math.max(0, runningStock);

    // 각 월별 누적 재고 계산
    months.forEach(monthData => {
      runningStock += monthData.입고 - monthData.출고;
      monthData.재고수량 = Math.max(0, runningStock);
      monthData.재고금액 = Math.round(monthData.재고수량 * avgPrice);
    });

    setMonthlyTrend(months);
  };

  const categoryData = Object.entries(stats.byCategory).map(([name, value]) => ({
    name,
    value
  }));

  const locationData = Object.entries(stats.byLocation).map(([name, value]) => ({
    name,
    재고수: value
  }));

  const statusData = Object.entries(stats.byStatus).map(([name, value]) => ({
    name,
    수량: value
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-brand-900">OOM 통합관리</h2>
          <p className="text-brand-500 mt-1">재고, 매출, 프라이스리스트를 한눈에</p>
        </div>
        <div className="text-sm text-brand-400 bg-white px-4 py-2 rounded-full shadow-sm border border-brand-100">
          마지막 업데이트: {new Date().toLocaleString('ko-KR')}
        </div>
      </div>

      {/* 알림 배너 */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Icons.Bell className="w-6 h-6 text-amber-600 mr-3" />
              <h3 className="text-lg font-semibold text-brand-900">
                알림 ({alerts.length}건)
              </h3>
            </div>
            <Link
              to="/alerts"
              className="text-sm text-brand-600 hover:text-brand-700 font-medium transition"
            >
              설정 관리 →
            </Link>
          </div>
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
            {alerts.slice(0, 10).map(alert => (
              <AlertBanner
                key={alert.id}
                alert={alert}
                onDismiss={() => handleDismissAlert(alert.id)}
              />
            ))}
            {alerts.length > 10 && (
              <p className="text-center text-sm text-brand-500 pt-2">
                +{alerts.length - 10}개의 알림이 더 있습니다
              </p>
            )}
          </div>
        </div>
      )}

      {/* 주요 지표 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="전체 재고"
          value={stats.total}
          icon={Icons.Box}
          color="text-blue-600"
          bg="bg-blue-50"
        />
        <StatCard
          title="재고 가치"
          value={`${stats.totalValue.toLocaleString()}원`}
          icon={Icons.DollarSign}
          color="text-emerald-600"
          bg="bg-emerald-50"
        />
        <StatCard
          title="이번 달 판매"
          value={`${salesStats.thisMonth}건`}
          subtitle={`${salesStats.thisMonthRevenue.toLocaleString()}원`}
          icon={Icons.Activity}
          color="text-amber-600"
          bg="bg-amber-50"
        />
      </div>

      {/* 빠른 작업 버튼 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
        <h3 className="text-lg font-semibold mb-4 text-brand-900">빠른 작업</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <QuickActionLink to="/add" icon={Icons.Add} label="재고 등록" />
          <QuickActionLink to="/scan" icon={Icons.Scan} label="바코드 스캔" />
          <QuickActionLink to="/inventory" icon={Icons.Inventory} label="재고 조회" />
          <QuickActionLink to="/sales" icon={Icons.Sales} label="판매 내역" />
        </div>
      </div>

      {/* 차트 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 카테고리별 분포 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-lg font-semibold mb-6 text-brand-900">카테고리별 분포</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={categoryData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12 }}
                width={80}
              />
              <Tooltip
                cursor={{ fill: '#f1f5f9' }}
                contentStyle={{
                  borderRadius: '8px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
              />
              <Bar dataKey="value" name="수량" barSize={20} radius={[0, 4, 4, 0]}>
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* 위치별 재고 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-lg font-semibold mb-6 text-brand-900">위치별 재고 현황</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={locationData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#64748b', fontSize: 12 }}
                width={80}
              />
              <Tooltip
                cursor={{ fill: '#f1f5f9' }}
                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              />
              <Bar dataKey="재고수" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 월별 재고 추이 */}
      <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
        <h3 className="text-lg font-semibold mb-6 text-brand-900">월별 재고 추이 (최근 12개월)</h3>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={monthlyTrend} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} />
            <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11 }} width={40} />
            <Tooltip
              cursor={{ fill: '#f1f5f9' }}
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              formatter={(value, name) => {
                if (name === '재고금액') return [`${value.toLocaleString()}원`, name];
                return [`${value.toLocaleString()}개`, name];
              }}
            />
            <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
            <Bar yAxisId="left" dataKey="입고" fill="#3b82f6" name="월 입고량" radius={[4, 4, 0, 0]} barSize={20} />
            <Bar yAxisId="left" dataKey="출고" fill="#ef4444" name="월 출고량" radius={[4, 4, 0, 0]} barSize={20} />
            <Line 
              yAxisId="left" 
              type="monotone" 
              dataKey="재고수량" 
              stroke="#10b981" 
              name="누적 재고" 
              strokeWidth={2.5}
              dot={{ r: 4, fill: '#10b981' }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* 상태별 재고 & 최근 활동 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 상태별 재고 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-lg font-semibold mb-6 text-brand-900">상태별 재고</h3>
          <div className="space-y-4">
            {statusData.map((item, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-brand-700 font-medium">{item.name}</span>
                <div className="flex items-center space-x-3 flex-1 mx-4">
                  <div className="flex-1 bg-brand-100 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-brand-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${(item.수량 / stats.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
                <span className="text-sm font-bold text-brand-900 w-12 text-right">
                  {item.수량}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 최근 활동 */}
        <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-6">
          <h3 className="text-lg font-semibold mb-6 text-brand-900">최근 활동</h3>
          <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
            {recentActivity.map(activity => (
              <div key={activity.id} className="flex items-start space-x-4 p-3 hover:bg-brand-50 rounded-lg transition-colors">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${activity.changeType === '등록' ? 'bg-blue-100 text-blue-600' :
                    activity.changeType === '판매완료' ? 'bg-green-100 text-green-600' :
                      activity.changeType === '위치변경' ? 'bg-purple-100 text-purple-600' :
                        'bg-gray-100 text-gray-600'
                  }`}>
                  {activity.changeType === '등록' && <Icons.Add className="w-5 h-5" />}
                  {activity.changeType === '판매완료' && <Icons.DollarSign className="w-5 h-5" />}
                  {activity.changeType === '위치변경' && <Icons.Location className="w-5 h-5" />}
                  {activity.changeType === '상태변경' && <Icons.Inventory className="w-5 h-5" />}
                  {activity.changeType === '수정' && <Icons.Dashboard className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-brand-900 font-medium truncate">
                    {activity.productId ? (
                      <Link to={`/inventory/${activity.productId}`} className="hover:text-brand-600 hover:underline">
                        {activity.productName}
                      </Link>
                    ) : (
                      activity.productName
                    )} - {activity.changeType}
                  </p>
                  {activity.previousValue && activity.newValue && (
                    <p className="text-brand-500 text-sm truncate">
                      {activity.previousValue} → {activity.newValue}
                    </p>
                  )}
                  {activity.changeType === '수정' && activity.changes && (
                    <p className="text-brand-500 text-sm truncate">
                      수정: {Object.keys(activity.changes).join(', ')}
                    </p>
                  )}
                  <p className="text-brand-400 text-xs mt-1">
                    {activity.userId && <span className="text-brand-600 font-medium">{getUserDisplayName(activity.userId)} · </span>}
                    {activity.timestamp.toLocaleString('ko-KR')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, bg }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-brand-100 p-4 sm:p-6 hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-brand-500 text-sm font-medium mb-1">{title}</p>
          <p className="text-lg sm:text-xl md:text-2xl font-bold text-brand-900 break-all">{value}</p>
          {subtitle && (
            <p className="text-xs sm:text-sm text-brand-400 mt-1 break-all">{subtitle}</p>
          )}
        </div>
        <div className={`${bg} ${color} p-2 sm:p-3 rounded-xl flex-shrink-0`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
      </div>
    </div>
  );
}

function QuickActionLink({ to, icon: Icon, label }) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center justify-center p-6 bg-brand-50 hover:bg-white border border-transparent hover:border-brand-200 rounded-xl transition-all duration-200 group"
    >
      <div className="p-3 bg-white rounded-full shadow-sm group-hover:scale-110 transition-transform duration-200 mb-3">
        <Icon className="w-6 h-6 text-brand-600" />
      </div>
      <span className="text-sm font-medium text-brand-700 group-hover:text-brand-900">{label}</span>
    </Link>
  );
}

function AlertBanner({ alert, onDismiss }) {
  const severityConfig = {
    high: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: 'text-red-600',
      badge: 'bg-red-100 text-red-800'
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      icon: 'text-amber-600',
      badge: 'bg-amber-100 text-amber-800'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: 'text-blue-600',
      badge: 'bg-blue-100 text-blue-800'
    }
  };

  const config = severityConfig[alert.severity] || severityConfig.info;

  const alertTypeLabels = {
    low_stock: '저재고',
    long_term_unsold: '장기 미판매',
    reservation_expiry: '예약 만료',
    consignment_settlement: '위탁 정산'
  };

  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4 transition-all hover:shadow-sm`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <span className={`${config.badge} text-xs font-medium px-2 py-1 rounded-full mr-2`}>
              {alertTypeLabels[alert.type] || alert.type}
            </span>
            {alert.itemName && (
              <Link
                to={`/inventory/${alert.itemId}`}
                className={`${config.text} text-sm font-semibold hover:underline`}
              >
                {alert.itemName}
              </Link>
            )}
          </div>
          <p className={`${config.text} text-sm`}>{alert.message}</p>
          {alert.data && (
            <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-3">
              {alert.data.location && <span>📍 {alert.data.location}</span>}
              {alert.data.category && <span>🏷️ {alert.data.category}</span>}
              {alert.data.currentQuantity !== undefined && <span>📦 {alert.data.currentQuantity}개</span>}
              {alert.data.daysUnsold && <span>📅 {alert.data.daysUnsold}일</span>}
              {alert.data.daysRemaining !== undefined && <span>⏰ {alert.data.daysRemaining}일 남음</span>}
            </div>
          )}
        </div>
        <button
          onClick={onDismiss}
          className={`${config.icon} hover:opacity-70 transition ml-3 flex-shrink-0`}
          title="알림 무시"
        >
          <Icons.X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default DashboardEnhanced;
